import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Loader from "../../components/loaders/Loader";
import Tabs, { TabButton } from "../../components/tabs/Tabs";
import { fetchPublicPricing } from "../../services/api/membership";
import { PricingData, SubscriptionFrequencies, Plan, SubscriptionFeature, FrequencyDetail } from "../../types/membership/membershipTypes";
import { toValidNumber } from "../../utils/helpers";
import { useMediaQuery } from "react-responsive";
import { isDesktopMediaQuery } from "../../constants/constants";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../redux-stores/store";
import { setBGWhite, setIntendedPath } from "../../redux-stores/slices/layoutSlice";


const PricingPublic: React.FC = () => {
    const location = useLocation();

    const [loading, setLoading] = useState<boolean>(false);
    const [pricing, setPricing] = useState<PricingData | null>(null);
    const [frequency, setFrequency] = useState<SubscriptionFrequencies>(SubscriptionFrequencies.MONTHLY);
    const [durationWisePricing, setDurationWisePricing] = React.useState<{ plan: Plan, frequency: SubscriptionFrequencies } | null>(null);
    const dispatch = useDispatch();

    const isDesktop = useMediaQuery(isDesktopMediaQuery);

    const { isAuthenticated } = useSelector((state: RootState) => state.auth);
    const navigate = useNavigate();

    const fetchPricingData = async () => {
        try {
            setLoading(true);
            const pricingData = await fetchPublicPricing();
            setPricing(pricingData);
        } catch (error) {
            console.log(error);
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        if (durationWisePricing) {
            dispatch(setBGWhite(true));
        } else {
            dispatch(setBGWhite(false));
        }
    }, [durationWisePricing])

    useEffect(() => {
        fetchPricingData();
    }, [])

    useEffect(() => {
        if (isAuthenticated) {
            navigate("/i/pricing");
        }
    }, [isAuthenticated])

    const getSortedFrequenciesByPrice = (plan: Plan) => {
        if (!plan.frequencies) return [];

        const frequenciesArray: (FrequencyDetail & { slug: string })[] = [];

        for (let [freq, details] of Object.entries(plan.frequencies)) {
            frequenciesArray.push({ ...details, slug: freq });
        }

        return frequenciesArray.sort((a, b) => Number(a.amount) - Number(b.amount));

    }


    const getFrequenciesDataAsTabButtons = () => {
        let buttons: TabButton[] = [];
        if (pricing?.frequencies) {
            for (let [id, content] of Object.entries(pricing.frequencies)) {
                buttons.push({ id, content })
            }
        }
        return buttons;
    }

    const updateIntendedURL = () => {
        dispatch(setIntendedPath(location.pathname + location.search));
    }


    if (loading) return <Loader isLoading={true} />

    return (
        <div className="min-full-screen">
            {
                durationWisePricing ? (
                    <>
                        <div className="m-3">
                            <div className="d-flex align-items-center justify-content-start gap-2 mb-2">
                                <button onClick={() => setDurationWisePricing(null)} className="text-dark btn-link"> <i className="fa fa-arrow-left fs-14"></i> </button>
                                <h6 className="m-0">{durationWisePricing?.plan.name}</h6>
                            </div>
                            <p>{durationWisePricing?.plan?.description}</p>
                            {
                                getSortedFrequenciesByPrice(durationWisePricing?.plan)?.map((freq) => (
                                    <div
                                        key={freq.id}
                                        onClick={() => setDurationWisePricing({ plan: durationWisePricing.plan, frequency: freq.slug as SubscriptionFrequencies })}
                                        className={`cursor-pointer p-3 mb-3 rounded-4 position-relative ${freq.slug === durationWisePricing.frequency ? "bg-primary text-white" : "border border-dark"}`}
                                        style={{ borderRadius: "18px" }}
                                    >

                                        {/* Discount Badge */}
                                        {freq.discount > 0 && (
                                            <span
                                                className="position-absolute translate-middle badge bg-success text-white fs-14 py-2"
                                                style={{ borderRadius: "1rem", right: '-25px', }}
                                            >
                                                -{freq.discount}%
                                            </span>
                                        )}

                                        {/* Title */}
                                        <div className="fw-semibold small">{freq.name}</div>

                                        {/* Amount Row */}
                                        <div className="d-flex align-items-center gap-2 mt-1">
                                            <div className="fs-4 fw-bold">₹{freq.amount}</div>

                                            {freq.original_amount && Number(freq.original_amount) > Number(freq.amount) && (
                                                <div className="text-decoration-line-through fs-14">
                                                    ₹{freq.original_amount}
                                                </div>
                                            )}
                                            {
                                                freq.slug !== SubscriptionFrequencies.MONTHLY &&
                                                <div className="fs-14">
                                                    (₹{freq.monthly_amount} / month)
                                                </div>
                                            }
                                        </div>
                                    </div>
                                ))
                            }
                            <Link onClickCapture={updateIntendedURL} style={{ height: '48px' }} className="btn btn-primary rounded-4 bg-primary border-primary w-100 d-flex align-items-center gap-2 justify-content-center" to={'/register'}>Join Now</Link>
                        </div>
                    </>
                ) :
                    (
                        <div className="d-flex flex-column align-item-center" style={{ minHeight: 'calc(100vh - 180px)' }}>
                            <h1 className="pop fw-bolder mx-auto">PICK YOUR PLAN</h1>
                            <p className="mx-auto ">Select a plan that fits your needs</p>
                            <div className="p-2">
                                {/* <div className="d-flex justify-content-center align-item-center">
                                    <Tabs buttons={getFrequenciesDataAsTabButtons()} buttonClass={`mx-3 ${isDesktop ? 'w-50' : 'w-100'} mb-4`} onChange={(fq: SubscriptionFrequencies) => setFrequency(fq)} />
                                </div> */}
                                <div className="d-flex justify-content-center align-item-center">
                                    <div className="row" style={{ width: isDesktop ? '80%' : '100%' }}>
                                        {
                                            pricing?.plans && pricing.plans.map((plan, i) => {
                                                return (
                                                    <div key={i} className="col-xxl-4 p-0">
                                                        <div className={`card card-style py-4 mx-1 position-relative ${plan.featured ? 'border border-green-dark' : ''}`} style={{ paddingTop: '26px', paddingBottom: '26px' }}>
                                                            <div className="px-4">
                                                                {/* <img src="images/undraw/1.svg" width="250" className="mx-auto" /> */}
                                                                <div className="d-flex justify-content-start align-items-center" style={{ minHeight: '3rem', }}>
                                                                    <h1 className="font-40" style={{ fontSize: isDesktop ? '60px' : '40px' }}>
                                                                        <sup className="price-sup font-22">₹</sup>
                                                                        {toValidNumber(plan.frequencies?.[frequency]?.amount)}
                                                                    </h1>
                                                                    {
                                                                        plan.frequencies?.[frequency]?.original_amount !== plan.frequencies?.[frequency]?.amount &&
                                                                        <div className="d-flex flex-column ms-2">
                                                                            <span className="fw-semibold font-20 opacity-50 " data-kt-element="period" style={{ textDecoration: "line-through" }}>₹ {plan['frequencies']?.[frequency]?.['original_amount']}</span>
                                                                            <span className="color-highlight">({plan['frequencies']?.[frequency]?.['discount']}% off)</span>
                                                                        </div>
                                                                    }
                                                                </div>
                                                                <h4 className="font-26 pop mt-2">{plan?.name}</h4>
                                                                <p className="text-gray-500 mb-2">{plan?.description ?? 'A simple Start for Everyone'}</p>

                                                            </div>
                                                            <div className="divider my-2"></div>
                                                            <div className="px-3 my-2">
                                                                {
                                                                    pricing.features && pricing.features.map((sf: SubscriptionFeature, i: number) => {
                                                                        return (
                                                                            <div className="text-gray-800 my-3" key={i}>
                                                                                {
                                                                                    plan.features?.[sf.id] ? <i className="fa fa-check-circle color-green-dark me-2"></i> : <i className="fa fa-times-circle color-red-dark me-2"></i>
                                                                                }
                                                                                <span>{sf.name}</span>
                                                                                {
                                                                                    sf.type === 'consumable' && <span>- <b>{plan.features?.[sf.id]?.value}</b> per month</span>
                                                                                }
                                                                            </div>
                                                                        )
                                                                    })
                                                                }
                                                            </div>
                                                            <div className="d-flex justify-content-center">
                                                                {
                                                                    <button onClick={e => setDurationWisePricing({ plan, frequency })} className={`btn w-100 mx-3 text-center rounded-sm btn-sm ${plan.featured ? 'bg-green-dark' : 'btn-primary'}`}>Start with {plan.name}</button>
                                                                }
                                                            </div>
                                                        </div>
                                                    </div>
                                                )
                                            })
                                        }
                                    </div>
                                </div>


                            </div>
                        </div>
                    )
            }
        </div>
    )
}

export default PricingPublic